import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { SelectorVistas } from '@xiltrion/ui/bloques/selector-vistas';
import { EstadoError } from '@xiltrion/ui/bloques/estados';
import type { NombreIcono } from '@xiltrion/ui/icono';
import { apiDelete, apiPatch, apiPost } from '@/api/client';
import { avisarError, useTasks } from '@/api/hooks';
import type { ProjectDetail, View } from '@/api/types';
import { registry } from '@/app/extension-points';
import { useRealtime } from '@/app/realtime';
import { useSession } from '@/app/session';
import { TaskDetail } from '../tasks/TaskDetail';
import { ViewFilters } from './ViewFilters';

/**
 * Pestañas de vistas sobre las mismas tareas (spec §3.1). Todas las vistas leen la misma consulta de tareas; cada una
 * aplica su configuración. La vista y la tarea abiertas viajan en la URL (?vista=…&tarea=…).
 */
export function ViewHost({ scope, project, views, lastViewId, taskQuery, canManage, invalidateViews, header }: {
  scope: 'project' | 'my_tasks' | 'portfolio';
  project: ProjectDetail | null;
  views: View[];
  lastViewId: string | null;
  taskQuery: Record<string, unknown>;
  canManage: boolean;
  invalidateViews: () => void;
  header?: React.ReactNode;
}) {
  const qc = useQueryClient();
  const { has } = useSession();
  const [params, setParams] = useSearchParams();
  const available = views.filter((v) => !!registry.viewType(v.type) && has(registry.viewType(v.type)!.module));
  const active = available.find((v) => v.id === params.get('vista')) ?? available.find((v) => v.id === lastViewId) ?? available.find((v) => v.isDefault) ?? available[0] ?? null;
  const def = active ? registry.viewType(active.type) : undefined;
  const tasksQ = useTasks({ ...(taskQuery as object), completed: 'all' } as never, !!def?.needsTasks || !active);
  useRealtime(project ? [project.id] : [...new Set((tasksQ.data?.items ?? []).map((t) => t.projectId))]);
  const taskId = params.get('tarea');
  const setParam = (k: string, v: string | null) => setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: k === 'tarea' ? false : true });
  useEffect(() => {
    if (active && active.id !== lastViewId) void apiPost(`/views/${active.id}/open`).catch(() => undefined);
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const types = useMemo(() => registry.viewTypes().filter((v) => v.scopes.includes(scope) && has(v.module)), [scope, has]);
  const createPath = scope === 'project' && project ? `/projects/${project.id}/views` : '/views/my-tasks';
  const View = def?.component;
  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      <SelectorVistas
        className="px-4"
        vistas={available.map((v) => ({ id: v.id, nombre: v.name, icono: (registry.viewType(v.type)?.icon ?? 'mdi:table') as NombreIcono, personal: !!v.ownerId && scope === 'project', predeterminada: v.isDefault, oculta: v.hidden }))}
        activa={active?.id ?? null}
        onElegir={(id) => setParam('vista', id)}
        tipos={types.map((t) => ({ tipo: t.type, nombre: t.label, icono: t.icon, descripcion: t.description }))}
        puedeAdministrar={canManage}
        onAgregar={(tipo, personal) => void apiPost<View>(createPath, { type: tipo, name: registry.viewType(tipo)?.label ?? tipo, personal }).then((v) => { invalidateViews(); setParam('vista', v.id); }, avisarError)}
        onRenombrar={(id, nombre) => void apiPatch(`/views/${id}`, { name: nombre }).then(invalidateViews, avisarError)}
        onMover={(m) => void apiPost(`/views/${m.id}/move`, { afterId: m.despuesDe, beforeId: m.despuesDe ? null : m.antesDe }).then(invalidateViews, avisarError)}
        onOcultar={(id, oculta) => void apiPatch(`/views/${id}`, { hidden: oculta }).then(invalidateViews, avisarError)}
        onPredeterminada={(id) => void apiPatch(`/views/${id}`, { isDefault: true }).then(invalidateViews, avisarError)}
        onBorrar={(id) => void apiDelete(`/views/${id}`).then(() => { invalidateViews(); setParam('vista', null); }, avisarError)}
        derecha={active && def?.needsTasks ? <ViewFilters view={active} project={project} editable={canManage || !!active.ownerId} onSaved={invalidateViews} /> : undefined}
      />
      <div className="min-h-0 flex-1 overflow-auto">
        {tasksQ.error ? <div className="p-6"><EstadoError error={tasksQ.error} onReintentar={() => void tasksQ.refetch()} /></div>
          : View ? <View project={project} tasks={tasksQ.data?.items ?? []} view={active ? { id: active.id, type: active.type, config: active.config } : null} onOpenTask={(id) => setParam('tarea', id)} loading={tasksQ.isLoading} /> : null}
      </div>
      <TaskDetail taskId={taskId} onClose={() => { setParam('tarea', null); void qc.invalidateQueries({ queryKey: ['tasks'] }); }} onOpenTask={(id) => setParam('tarea', id)} />
      {project && <span className="sr-only" aria-live="polite">{tasksQ.isFetching ? 'Actualizando' : ''}</span>}
    </div>
  );
}
